import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PANEL_TYPES} from '../state/informeModel';

// Lo que mide el desplegable abierto, para decidir si entra abajo del botón. No hace falta que sea
// exacto: es el umbral con el que se elige el lado.
const ALTO_MENU = 380;

// Los tipos del picker del mockup, con sus íconos (SVG inline: el set de FA del repo no los tiene).
// El sexto, «Texto», llegó con la card 1YGcVgVw: insights y recomendaciones escritos por la agencia.
export const PANEL_TYPE_OPTIONS = [
    {type: PANEL_TYPES.KPI, nameKey: 'informes.panel.type.kpi', name: 'Métrica destacada',
     descKey: 'informes.panel.type.kpi_desc', desc: 'Un número grande con su variación, en la fila de métricas clave.',
     icon: 'M4 19V9M10 19V5M16 19v-8M22 19H2'},
    {type: PANEL_TYPES.BARS, nameKey: 'informes.panel.type.bars', name: 'Gráfico de barras',
     descKey: 'informes.panel.type.bars_desc', desc: 'Compara una métrica entre campañas, plataformas o líneas de seguimiento.',
     icon: 'M3 10h4v10H3zM10 4h4v16h-4zM17 13h4v7h-4z'},
    {type: PANEL_TYPES.PIE, nameKey: 'informes.panel.type.pie', name: 'Gráfico de torta',
     descKey: 'informes.panel.type.pie_desc', desc: 'Cómo se reparte una métrica entre los valores de una dimensión.',
     icon: 'M21 12A9 9 0 1 1 12 3v9zM21 12h-9V3a9 9 0 0 1 9 9z'},
    {type: PANEL_TYPES.LINE, nameKey: 'informes.panel.type.line', name: 'Evolución (líneas)',
     descKey: 'informes.panel.type.line_desc', desc: 'La métrica a lo largo del tiempo, comparada con el período anterior.',
     icon: 'M3 17l5-6 4 3 6-8M3 21h18'},
    {type: PANEL_TYPES.TABLE, nameKey: 'informes.panel.type.table', name: 'Tabla',
     descKey: 'informes.panel.type.table_desc', desc: 'El detalle fila por fila. Se puede ubicar en cualquier lugar del informe.',
     icon: 'M3 4h18v16H3zM3 10h18M9 10v10'},
    {type: PANEL_TYPES.TEXT, nameKey: 'informes.panel.type.text', name: 'Texto',
     descKey: 'informes.panel.type.text_desc', desc: 'Insights, cambios del período o recomendaciones para tu cliente.',
     icon: 'M4 6h16M4 10h16M4 14h10M4 18h7'},
];

// El ícono de los sugeridos: dos columnas enfrentadas (plataforma | GA4).
const PRESET_ICON = 'M3 4h18v16H3zM12 4v16M6 9h3M6 13h3M15 9h3M15 13h3';

/**
 * El desplegable de los 5 tipos de bloque. `onAdd(type)` agrega el panel donde lo diga quien lo
 * monta: el «+ agregar acá» de una fila con lugar, o el botón al pie del folio.
 *
 * `presets` (`state/presets.js`) son los bloques que nacen ya armados para una pregunta: van
 * debajo de los tipos, bajo «Sugeridos», y `onAddPreset(preset)` los agrega en el mismo lugar.
 *
 * Hasta el 07/09 esto era una TARJETA que ocupaba un lugar en la grilla (`PANEL_TYPES.ADD`, span
 * 6, arrastrable, con su `add_index` persistido) y el bloque nuevo se insertaba en SU índice,
 * empujándola: por eso Pablo la vio «subir» y «desaparecer de abajo» (card PlFYrmTo).
 */
export function AddPanelMenu({onAdd, variant = 'row', label = null, ariaLabel = null, disabledTypes = null,
                              presets = null, onAddPreset = null}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [haciaArriba, setHaciaArriba] = useState(false);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);

    // Abrir SIEMPRE hacia abajo dejaba el menú fuera de la ventana cuando el botón queda cerca del
    // borde inferior — y como el folio termina ahí, la página no scrollea: cuatro de las cinco
    // opciones eran inalcanzables con el mouse (QA-editor-09, «rompe»). Se mide el espacio real
    // ANTES de pintar, así que el menú no se ve saltar.
    useLayoutEffect(() => {
        if (!open || !buttonRef.current) return;
        const r = buttonRef.current.getBoundingClientRect();
        const abajo = window.innerHeight - r.bottom;
        // Cada sugerido suma una fila (y el rótulo «Sugeridos»): el menú crece hacia abajo.
        const alto = ALTO_MENU + (presets && presets.length ? 24 + presets.length * 56 : 0);
        setHaciaArriba(abajo < alto && r.top > abajo);
    }, [open, presets]);

    useEffect(() => {
        if (!open) return undefined;
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    return (
        <div className={`inf-add inf-add--${variant}`} ref={wrapperRef} data-testid={`add-panel-${variant}`}>
            {/* `ariaLabel` distingue los N «agregar aquí» de la pantalla: sin él, un lector de
                pantalla lista el mismo nombre varias veces sin decir después de qué bloque va cada
                uno (review C-editor-07b). */}
            <button type="button" className="inf-add-button" aria-haspopup="menu" aria-expanded={open}
                    ref={buttonRef} aria-label={ariaLabel || undefined} onClick={() => setOpen(!open)}>
                <span className="inf-add-plus" aria-hidden="true">+</span>
                {/* El rótulo va en su propio span porque el affordance de fila lo esconde en
                    reposo y deja sólo el «+»: el slot tiene que verse —si no, no se descubre, y
                    en una tablet no hay hover que lo revele— sin llenar el folio de texto. El
                    botón conserva su nombre accesible igual, que es lo que lee un lector de
                    pantalla y lo que buscan los tests. */}
                <span className="inf-add-label">{label || t('informes.panel.add', 'Agregar gráfico, métrica o texto')}</span>
            </button>
            {open && (
                <div className={`inf-add-menu ${haciaArriba ? '-arriba' : ''}`} role="menu">
                    {PANEL_TYPE_OPTIONS.map((option) => {
                        // Un gráfico de barras o de torta sin dimensiones nace sin dimensión, el
                        // backend lo rechaza y el selector del bloque queda con CERO opciones: el
                        // usuario no puede arreglarlo desde ahí. Se ofrece deshabilitado con el
                        // motivo, en vez de dejar que el gesto termine en un 422 (review C-editor-05).
                        const bloqueado = !!(disabledTypes && disabledTypes[option.type]);
                        return (
                        <button key={option.type} type="button" className={`inf-add-item ${bloqueado ? '-off' : ''}`}
                                role="menuitem" disabled={bloqueado} title={bloqueado ? disabledTypes[option.type] : undefined}
                                onClick={() => { setOpen(false); onAdd(option.type); }}>
                            <span className="inf-add-item-icon" aria-hidden="true">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d={option.icon}/>
                                </svg>
                            </span>
                            <span>
                                <strong>{t(option.nameKey, option.name)}</strong>
                                <small>{bloqueado ? disabledTypes[option.type] : t(option.descKey, option.desc)}</small>
                            </span>
                        </button>
                        );
                    })}
                    {/* Dentro de un `menu` sólo van ítems o grupos: el rótulo visible es decorativo y
                        el nombre del grupo lo da `aria-label`. */}
                    {presets && presets.length > 0 && onAddPreset && (
                        <div role="group" aria-label={t('informes.presets.section', 'Sugeridos')} data-testid="add-presets">
                            <div className="inf-add-section" aria-hidden="true">{t('informes.presets.section', 'Sugeridos')}</div>
                            {presets.map((preset) => (
                                <button key={preset.id} type="button" className="inf-add-item -preset" role="menuitem"
                                        data-testid={`add-preset-${preset.id}`}
                                        onClick={() => { setOpen(false); onAddPreset(preset); }}>
                                    <span className="inf-add-item-icon" aria-hidden="true">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d={PRESET_ICON}/>
                                        </svg>
                                    </span>
                                    <span>
                                        <strong>{t(preset.nameKey, preset.name)}</strong>
                                        <small>{t(preset.descKey, preset.desc)}</small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function GripIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="8" cy="6" r="1.6"/><circle cx="16" cy="6" r="1.6"/>
            <circle cx="8" cy="12" r="1.6"/><circle cx="16" cy="12" r="1.6"/>
            <circle cx="8" cy="18" r="1.6"/><circle cx="16" cy="18" r="1.6"/>
        </svg>
    );
}
